import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CalendarDaysIcon, CalendarIcon } from "@heroicons/react/24/outline";

/**
 * Modal to configure Date widget type when dropped or edited.
 * Asks whether the date should be:
 * - "signature": Auto-filled with signing date when user clicks (current flow)
 * - "free": User can select any date via date picker
 */
function DateWidgetOption({
  isOpen,
  onClose,
  onSave,
  initialDateInputType = "free",
}) {
  const [dateInputType, setDateInputType] = useState(initialDateInputType);

  useEffect(() => {
    if (isOpen) {
      setDateInputType(initialDateInputType);
    }
  }, [isOpen, initialDateInputType]);

  const handleSave = () => {
    onSave(dateInputType);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Date Widget Type
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  Choose how the date field will behave when signing:
                </p>

                <div className="mt-4 space-y-3">
                  <label
                    className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      dateInputType === "free"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="dateInputType"
                      value="free"
                      checked={dateInputType === "free"}
                      onChange={() => setDateInputType("free")}
                      className="mt-1 h-4 w-4 text-blue-600"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-blue-600" />
                        <span className="font-medium text-gray-900">
                          Selectable Date
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Signer can select any date using a date picker when they
                        click on the field.
                      </p>
                    </div>
                  </label>

                  <label
                    className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      dateInputType === "signature"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="dateInputType"
                      value="signature"
                      checked={dateInputType === "signature"}
                      onChange={() => setDateInputType("signature")}
                      className="mt-1 h-4 w-4 text-blue-600"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
                        <span className="font-medium text-gray-900">
                          Signature Date
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Date is auto-filled with the current date when the
                        signer clicks. No date picker.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSave}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default DateWidgetOption;
